import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { publicRequest, privateRequest } from "../utils/api";

// Types based on backend models and API responses
export interface AboutTranslation {
  id?: number;
  about_id?: number;
  locale: string;
  hero_title: string;
  hero_description?: string;
  hero_image?: string;
  story_title?: string;
  story_content?: string;
  mission_title?: string;
  mission_content?: string;
  vision_title?: string;
  vision_content?: string;
  contact_title?: string;
  contact_description?: string;
  contact_address?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_map_url?: string;
  section_body?: string;
}

export interface About {
  id: number;
  translations?: AboutTranslation[];
  created_at?: string;
  updated_at?: string;
}

export interface AboutResponse {
  id: string;
  hero_title: string;
  hero_description?: string;
  hero_image?: string;
  story_title?: string;
  story_content?: string;
  mission_title?: string;
  mission_content?: string;
  vision_title?: string;
  vision_content?: string;
  contact_title?: string;
  contact_description?: string;
  contact_address?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_map_url?: string;
  section_body?: string;
  translations?: AboutTranslation[];
  created_at?: string;
  updated_at?: string;
}

// Query keys
export const ABOUT_QUERY_KEY = ["about"];

// Fetch about data
const fetchAbout = async (locale?: string): Promise<AboutResponse> => {
  try {
    const params = locale ? { locale } : {};
    const response = await publicRequest.get("/about", { params });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching about:", error);
    throw error;
  }
};

// Fetch about data for admin (with translations)
const fetchAboutAdmin = async (id: number): Promise<AboutResponse> => {
  try {
    const response = await privateRequest.get(`/about/${id}?admin=true`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching about for admin:", error);
    throw error;
  }
};

// Create about data (admin only)
const createAbout = async (aboutData: {
  translations: AboutTranslation[];
}): Promise<{ about: AboutResponse }> => {
  try {
    const response = await privateRequest.post("/about", aboutData);
    return response.data.data;
  } catch (error) {
    console.error("Error creating about:", error);
    throw error;
  }
};

// Update about data (admin only)
const updateAbout = async ({
  id,
  aboutData,
}: {
  id: number;
  aboutData: { translations?: AboutTranslation[] };
}): Promise<{ about: AboutResponse }> => {
  try {
    const response = await privateRequest.put(`/about/${id}`, aboutData);
    return response.data.data;
  } catch (error) {
    console.error("Error updating about:", error);
    throw error;
  }
};

// Delete about data (admin only)
const deleteAbout = async (id: number): Promise<void> => {
  try {
    await privateRequest.delete(`/about/${id}`);
  } catch (error) {
    console.error("Error deleting about:", error);
    throw error;
  }
};

// Custom hooks
export const useAbout = (locale?: string) => {
  return useQuery({
    queryKey: [...ABOUT_QUERY_KEY, locale],
    queryFn: () => fetchAbout(locale),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useAboutAdmin = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...ABOUT_QUERY_KEY, "admin", id],
    queryFn: () => fetchAboutAdmin(id),
    enabled,
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateAbout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAbout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ABOUT_QUERY_KEY });
    },
  });
};

export const useUpdateAbout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAbout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ABOUT_QUERY_KEY });
    },
  });
};

export const useDeleteAbout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAbout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ABOUT_QUERY_KEY });
    },
  });
};
